import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import katex from "katex";
import "katex/dist/katex.min.css";
import { motion, AnimatePresence } from "framer-motion";
import { useEditorStore } from "../../stores/editor";
import { cn } from "../../lib/cn";

interface MathMatch {
  latex: string;
  displayMode: boolean;
  start: number;
  end: number;
}

function findMathAtPosition(doc: string, pos: number): MathMatch | null {
  // Try display math $$...$$ first
  const displayRegex = /\$\$([^$]+)\$\$/g;
  let match: RegExpExecArray | null;
  while ((match = displayRegex.exec(doc)) !== null) {
    if (pos >= match.index && pos <= match.index + match[0].length) {
      return {
        latex: match[1].trim(),
        displayMode: true,
        start: match.index,
        end: match.index + match[0].length,
      };
    }
  }

  // Try inline math $...$ (non-greedy, avoid matching $$)
  const inlineRegex = /(?<!\$)\$(?!\$)([^$\n]+?)\$(?!\$)/g;
  while ((match = inlineRegex.exec(doc)) !== null) {
    if (pos >= match.index && pos <= match.index + match[0].length) {
      return {
        latex: match[1].trim(),
        displayMode: false,
        start: match.index,
        end: match.index + match[0].length,
      };
    }
  }

  return null;
}

function tryRenderLatex(latex: string, displayMode: boolean): string | null {
  try {
    return katex.renderToString(latex, {
      displayMode,
      throwOnError: false,
      output: "html",
    });
  } catch {
    return null;
  }
}

interface TooltipState {
  html: string;
  x: number;
  y: number;
}

/**
 * Equation hover preview. When hovering over LaTeX math (detected by $ delimiters),
 * shows a rendered preview in a glass tooltip.
 */
export function EquationPreview() {
  const editorView = useEditorStore((s) => s.editorView);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastPosRef = useRef<number>(-1);

  const updateTooltip = useCallback(
    (clientX: number, clientY: number) => {
      if (!editorView) {
        setTooltip(null);
        return;
      }

      const pos = editorView.posAtCoords({ x: clientX, y: clientY });
      if (pos === null || pos === lastPosRef.current) return;
      lastPosRef.current = pos;

      const doc = editorView.state.doc.toString();
      const math = findMathAtPosition(doc, pos);
      if (!math || !math.latex) {
        setTooltip(null);
        return;
      }

      const html = tryRenderLatex(math.latex, math.displayMode);
      if (!html) {
        setTooltip(null);
        return;
      }

      const coords = editorView.coordsAtPos(math.start);
      if (!coords) {
        setTooltip(null);
        return;
      }

      setTooltip({
        html,
        x: coords.left,
        y: coords.top,
      });
    },
    [editorView],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(() => {
        updateTooltip(e.clientX, e.clientY);
        rafRef.current = null;
      });
    },
    [updateTooltip],
  );

  const handleMouseLeave = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    lastPosRef.current = -1;
    setTooltip(null);
  }, []);

  useEffect(() => {
    const dom = editorView?.dom;
    if (!dom) return;

    dom.addEventListener("mousemove", handleMouseMove);
    dom.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      dom.removeEventListener("mousemove", handleMouseMove);
      dom.removeEventListener("mouseleave", handleMouseLeave);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [editorView, handleMouseMove, handleMouseLeave]);

  if (!tooltip) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15, ease: [0.2, 0.8, 0.2, 1] }}
        className={cn(
          "fixed z-[70] pointer-events-none",
          "rounded-[var(--radius-lg)] p-3",
          "bg-[var(--bg-surface)]/95 backdrop-blur-[var(--blur-md)]",
          "border border-[var(--border-subtle)]",
          "shadow-[var(--shadow-lg)]",
          "[&_.katex]:text-base",
        )}
        style={{
          left: tooltip.x,
          top: tooltip.y - 8,
          transform: "translateY(-100%)",
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: tooltip.html }} />
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}
